import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import useGroupLabels from 'hooks/useGroupLabels';
import { formatGroupCell } from 'utils/groupLabel';
import style from './MostMeetPRsTable.module.css';

// Column structure for most meet PRs
const columns = [
  { key: 'firstName', label: 'First Name', centered: false },
  { key: 'lastName', label: 'Last Name', centered: false },
  // Headed Age or Year by the club's grouping, where the table is drawn.
  { key: 'age', centered: true },
  { key: 'gender', label: 'Gender', centered: true },
  { key: 'meetsWithMinPrs', label: 'Number of Meets', centered: true },
];

const MostMeetPRsTable = ({
  athleteData,
  setSearchText,
  minPRsBeaten,
  setMinPRsBeaten,
}) => {
  // The athlete's group column reads Age and "11", or Year and "Y6" for a club that groups by year level.
  const { groupingBasis, words } = useGroupLabels();
  const [tableData, setTableData] = useState([]);

  const [sortKeys, setSortKeys] = useState(
    columns.reduce((acc, col) => {
      acc[col.key] = false;
      return acc;
    }, {})
  );

  useEffect(() => {
    // Data is already sorted by server, just use as-is
    setTableData([...athleteData]);
    // Reset sort keys
    setSortKeys(
      columns.reduce((acc, col) => {
        acc[col.key] = col.key === 'meetsWithMinPrs'; // true = descending for meets
        return acc;
      }, {})
    );
  }, [athleteData]);

  const handleAthleteSearch = (e) => {
    setSearchText(e.target.value);
  };

  const handleMinPRsChange = (e) => {
    const value = parseInt(e.target.value, 10);
    setMinPRsBeaten(isNaN(value) || value < 1 ? 1 : value);
  };

  const sortTable = (key) => {
    // Create a copy to avoid mutating state directly
    const sortData = [...tableData].sort((a, b) => {
      if (['firstName', 'lastName', 'gender'].includes(key)) {
        // String comparison
        const aVal = (a[key] || '').toString().toLowerCase();
        const bVal = (b[key] || '').toString().toLowerCase();
        return sortKeys[key]
          ? bVal.localeCompare(aVal)
          : aVal.localeCompare(bVal);
      } else {
        // Numeric comparison
        return sortKeys[key] ? b[key] - a[key] : a[key] - b[key];
      }
    });

    setTableData(sortData);

    // Toggle sort direction
    setSortKeys({
      ...sortKeys,
      [key]: !sortKeys[key],
    });
  };

  // Generate PRs beaten options (1, 2, 3, 4, 5)
  const prsOptions = [];
  for (let i = 1; i <= 5; i += 1) {
    prsOptions.push(i);
  }

  return (
    <div className={style.tableCard}>
      {/* Header with Season info */}
      <div className={style.tableHeader}>
        <div className={style.seasonInfo}>
          Most Season Bests in a single meet
        </div>
      </div>

      {/* What the numbers mean */}
      <p className={style.description}>
        Each row is an athlete who set or beat at least {minPRsBeaten} season
        {minPRsBeaten === 1 ? ' best' : ' bests'} at one meet, and Number of Meets is how many meets this
        season they did that at. A season best counts when the result set or beat the athlete&apos;s season
        best in that event at the time it was recorded; equalling one does not count. Only meets that count
        for season bests are included.
      </p>

      {/* Search Bar */}
      <div className={style.searchBar}>
        <input
          type="text"
          className={style.searchInput}
          placeholder="Search athletes by name..."
          id="athleteSearch"
          onInput={handleAthleteSearch}
        />
        <div className={style.filterGroup}>
          <label htmlFor="minPRsBeaten" className={style.filterLabel}>
            Min SBs Beaten:
          </label>
          <select
            id="minPRsBeaten"
            className={style.selectInput}
            value={minPRsBeaten}
            onChange={handleMinPRsChange}
          >
            {prsOptions.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </div>
        <div className={style.athleteCount}>
          {tableData.length} {tableData.length === 1 ? 'Athlete' : 'Athletes'}
        </div>
      </div>

      {/* Table */}
      <div className={style.tableWrapper}>
        <table className={style.dataTable}>
          <thead>
            <tr>
              {columns.map((col, index) => (
                <th
                  key={index}
                  onClick={() => sortTable(col.key)}
                  className={col.centered ? style.centered : ''}
                >
                  {col.key === 'age' ? words.value : col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tableData.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  style={{
                    textAlign: 'center',
                    padding: '40px',
                    color: '#6c757d',
                  }}
                >
                  No best meet performance data available
                </td>
              </tr>
            ) : (
              tableData.map((athlete, index) => (
                <tr key={athlete.athleteID || index}>
                  <td className={style.athleteName}>{athlete.firstName}</td>
                  <td className={style.athleteName}>{athlete.lastName}</td>
                  <td className={style.centered}>{formatGroupCell(groupingBasis, athlete.age)}</td>
                  <td className={style.centered}>{athlete.gender}</td>
                  <td className={style.meetsCount}>{athlete.meetsWithMinPrs}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

MostMeetPRsTable.propTypes = {
  athleteData: PropTypes.array,
  setSearchText: PropTypes.func,
  minPRsBeaten: PropTypes.number,
  setMinPRsBeaten: PropTypes.func,
};

export default MostMeetPRsTable;
